<script setup>
import { useHead } from "@vueuse/head";
import { ref, computed, onMounted, watch, watchEffect } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

import EventCardComponent from "@/components/EventCardComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

import { useTheme } from "@/composables/useTheme";

// ======================================================
// Vuex
// ======================================================
const store = useStore();

const sub = computed(() => store.getters["tenant/tenantSub"]);
const tenant = computed(() => store.getters["tenant/tenant"]);
const loading = computed(() => store.getters["tenant/tenantLoading"]);
const error = computed(() => store.getters["tenant/tenantError"]);

// ======================================================
// Theme
// ======================================================
const { isLight, toggleMode } = useTheme();

// ======================================================
// i18n
// ======================================================
const { t, locale } = useI18n();

const setLocale = (code) => {
	locale.value = code;
	localStorage.setItem("trekko_lang", code);
};

// ======================================================
// State
// ======================================================
const tenantData = ref(null);
const eventsRef = ref([]);
const eventFilter = ref("upcoming");
const currentPage = ref(1);

// ======================================================
// Helpers
// ======================================================
function parseDateSafely(dateString) {
	if (!dateString) return new Date();

	let d = new Date(dateString);

	// Safari/MySQL fallback
	if (isNaN(d.getTime())) {
		d = new Date(dateString.replace(" ", "T"));
	}

	// Final fallback
	if (isNaN(d.getTime())) {
		console.warn("Invalid date:", dateString);
		return new Date();
	}

	return d;
}

function mapTenantEventToUI(e) {
	if (!e) return null;

	const isoDate = e.date || e.start_date || e.startDate || null;

	const dateObj = parseDateSafely(isoDate);

	const ticket =
		Array.isArray(e.tickets) && e.tickets.length ? e.tickets[0] : null;

	const amount = ticket ? Number(ticket.Amount ?? ticket.amount ?? 0) : 0;

	const tags = Array.isArray(e.categories)
		? Array.from(
			new Set(
				e.categories
					.map((c) => (typeof c === "string" ? c : c?.Name ?? c?.name ?? ""))
					.filter(Boolean),
			),)
		: [];

	const now = new Date();

	let cardStatus = "rsvp";

	if (e.status) {
		const s = e.status.toLowerCase();

		if (s === "live") {
			cardStatus = "live";
		} else if (s === "starting soon" || s === "soon") {
			cardStatus = "soon";
		} else if (s === "ended") {
			cardStatus = "ended";
		}
	} else if (dateObj <= now) {
		cardStatus = "ended";
	}

	const titleStr = e.title ?? e.Title ?? "Untitled Event";

	const imageUrl = e.image_url ?? e.imageUrl ?? e.image ?? null;

	const priceNum = isNaN(amount) ? 0 : amount;

	const venueStr = e.location_name ?? e.location ?? e.venue ?? "";

	return {
		id: e.event_id ?? e.ID ?? e.id ?? Math.random().toString(36).slice(2, 9),

		title: titleStr,

		date: dateObj,

		time:
			(e.start_time ? `${e.start_time}` : "") +
			(e.end_time ? ` - ${e.end_time}` : ""),

		location: venueStr,

		longitude: e.longitude ?? e.Longitude ?? "",

		latitude: e.latitude ?? e.Latitude ?? "",

		imageUrl,

		startTime: e.start_time ?? e.startTime ?? "",

		tags,

		status: cardStatus,

		isUpcoming: dateObj > now,

		raw: e,

		Title: titleStr,

		ImageURL: imageUrl,

		posterGhost: titleStr.toUpperCase(),

		Description: e.description ?? e.Description ?? "",

		venue: venueStr,

		price: priceNum === 0 ? t("common.free") : `$${priceNum.toFixed(2)}`,

		priceSub:
			Array.isArray(e.tickets) && e.tickets.length
				? e.tickets[0].Type ?? ""
				: "",

		attendees: [],

		attendeeCount: "",

		saved: false,
	};
}

// ======================================================
// Computed
// ======================================================
const filteredEvents = computed(() => {
	return eventsRef.value.filter((event) => {
		if (eventFilter.value === "upcoming") {
			return event.isUpcoming;
		}

		return !event.isUpcoming;
	});
});

watch(eventFilter, () => {
	currentPage.value = 1;
});

// ======================================================
// SEO WATCHER
// ======================================================
watch(
	tenantData,
	(val) => {
		if (!val?.name) return;

		const title = `${val.name} — Trekko`;

		const rawDesc =
			val.description || `Discover events organized by ${val.name} on Trekko.`;

		const description =
			rawDesc.replace(/<[^>]*>/g, "").slice(0, 155) +
			(rawDesc.length > 155 ? "…" : "");

		const image = val.logo_url || "https://trekko.xyz/favicon.png";

		const url = window.location.href;

		useHead({
			title,

			meta: [
				{
					name: "description",
					content: description,
				},
				{
					name: "keywords",
					content: `${val.name}, events, organizer, Trekko`,
				},
				{
					name: "robots",
					content: "index, follow",
				},

				// Open Graph
				{
					property: "og:type",
					content: "profile",
				},
				{
					property: "og:site_name",
					content: "Trekko",
				},
				{
					property: "og:title",
					content: title,
				},
				{
					property: "og:description",
					content: description,
				},
				{
					property: "og:image",
					content: image,
				},
				{
					property: "og:url",
					content: url,
				},

				// Twitter
				{
					name: "twitter:card",
					content: "summary_large_image",
				},
				{
					name: "twitter:site",
					content: "@gotrekko",
				},
				{
					name: "twitter:title",
					content: title,
				},
				{
					name: "twitter:description",
					content: description,
				},
				{
					name: "twitter:image",
					content: image,
				},
				{
					name: "twitter:image:alt",
					content: `${val.name} on Trekko`,
				},
			],

			link: [
				{
					rel: "canonical",
					href: url,
				},
			],
		});
	},
	{
		immediate: true,
		deep: true,
	},
);

// ======================================================
// TENANT DATA WATCHER
// ======================================================
watch(
	tenant,
	(val) => {
		console.log("WATCH TENANT:", val);

		if (!val) {
			tenantData.value = null;
			eventsRef.value = [];
			return;
		}

		tenantData.value = {
			name: val.name ?? val.Name ?? val.handle ?? val.title ?? "",

			email: val.email ?? val.Email ?? "",

			phone: val.phone ?? val.Phone ?? "",

			description: val.description ?? val.Description ?? "",

			logo_url:
				val.logo_url ??
				val.logoUrl ??
				val.Logo ??
				val.organizer_logos?.[0]?.LogoURL ??
				"",

			handle: val.handle ?? val.Handle ?? "",
		};

		console.log("======== DEBUG TENANT ========");
		console.log("RAW TENANT:", val);
		console.log("ACTIVE EVENTS:", val.active_events);
		console.log("PAST EVENTS:", val.past_events);
		console.log("==============================");

		let rawEvents = [];

		if (Array.isArray(val.active_events)) {
			rawEvents = [...val.active_events];
		} else if (Array.isArray(val.past_events)) {
			rawEvents = [...val.past_events];
		} else if (Array.isArray(val.events)) {
			rawEvents = [...val.events];
		}

		console.log("RAW EVENTS FINAL:", rawEvents);

		eventsRef.value = rawEvents
			.map((e, i) => {
				const mapped = mapTenantEventToUI(e);

				console.log("MAPPED EVENT", i, mapped);

				return mapped;
			})
			.filter(Boolean);

		console.log("FINAL eventsRef:", eventsRef.value);
	},
	{
		immediate: true,
		deep: true,
	},
);

// ======================================================
// Mounted
// ======================================================
onMounted(() => {
	console.log("Mounted TenantView");

	console.log("tenant from store:", tenant.value);
});

// ======================================================
// Actions
// ======================================================
const sendEmail = () => {
	if (!tenantData.value?.email) {
		return alert("Organizer email not available");
	}

	window.location.href = `mailto:${tenantData.value.email}`;
};

const openChat = () => {
	if (!tenantData.value?.phone) {
		return alert("Organizer phone not available");
	}

	const phone = tenantData.value.phone.replace(/\D/g, "");

	window.open(
		`https://wa.me/${phone}?text=${encodeURIComponent("Let's chat")}`,
		"_blank",
	);
};

const contactOrganizer = () => {
	if (!tenantData.value?.phone) {
		return alert("Organizer phone not available");
	}

	window.location.href = `tel:${tenantData.value.phone}`;
};

// ======================================================
// Debug
// ======================================================
watchEffect(() => {
	console.log("========== DEBUG ==========");
	console.log("sub:", sub.value);
	console.log("loading:", loading.value);
	console.log("error:", error.value);
	console.log("tenant:", tenant.value);
	console.log("tenantData:", tenantData.value);
	console.log("eventsRef:", eventsRef.value);
	console.log("filteredEvents:", filteredEvents.value);
	console.log("===========================");
});
</script>

<template>
	<div class="min-h-screen flex flex-col transition-colors duration-300 font-['Inter',sans-serif]"
		:class="isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]'">
		<!-- Imigongo background patterns -->
		<div class="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
			<svg class="absolute -top-10 -right-10 w-[420px] h-[420px]" :style="{ opacity: isLight ? 0.07 : 0.06 }"
				viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
					<rect x="40" y="0" width="28" height="28" transform="rotate(45 40  0)" />
					<rect x="80" y="0" width="28" height="28" transform="rotate(45 80  0)" />
					<rect x="120" y="0" width="28" height="28" transform="rotate(45 120 0)" />
					<rect x="20" y="40" width="28" height="28" transform="rotate(45 20  40)" />
					<rect x="60" y="40" width="28" height="28" transform="rotate(45 60  40)" />
					<rect x="100" y="40" width="28" height="28" transform="rotate(45 100 40)" />
					<rect x="140" y="40" width="28" height="28" transform="rotate(45 140 40)" />
					<rect x="40" y="80" width="28" height="28" transform="rotate(45 40  80)" />
					<rect x="80" y="80" width="28" height="28" transform="rotate(45 80  80)" />
					<rect x="120" y="80" width="28" height="28" transform="rotate(45 120 80)" />
					<rect x="20" y="120" width="20" height="20" transform="rotate(45 20  120)" />
					<rect x="60" y="120" width="20" height="20" transform="rotate(45 60  120)" />
					<rect x="100" y="120" width="20" height="20" transform="rotate(45 100 120)" />
					<rect x="140" y="120" width="20" height="20" transform="rotate(45 140 120)" />
				</g>
			</svg>
			<svg class="absolute -bottom-10 -left-10 w-[280px] h-[280px]" :style="{ opacity: isLight ? 0.06 : 0.05 }"
				viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g :stroke="isLight ? '#0f1f1c' : '#a8d5cc'" stroke-width="3" fill="none">
					<rect x="60" y="10" width="64" height="64" transform="rotate(45 60 10)" />
					<rect x="60" y="22" width="46" height="46" transform="rotate(45 60 22)" />
					<rect x="60" y="34" width="28" height="28" transform="rotate(45 60 34)" />
					<rect x="60" y="46" width="10" height="10" transform="rotate(45 60 46)" />
				</g>
			</svg>
		</div>

		<!-- Language switcher -->
		<div class="fixed top-4 right-4 z-50 flex gap-0.5 p-0.5 rounded-full border shadow-lg transition-colors duration-300"
			:class="isLight
					? 'bg-[#e4f4f0] border-[#d0ede8]'
					: 'bg-[#0f1f1c]/90 border-white/10'
				">
			<button v-for="lang in [
				{ code: 'en', label: 'EN' },
				{ code: 'it', label: 'IT' },
			]" :key="lang.code" @click="setLocale(lang.code)"
				class="px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider transition-all duration-200"
				:class="locale === lang.code
						? isLight
							? 'bg-white text-[#0d6b5e] shadow-sm'
							: 'bg-white/10 text-white'
						: isLight
							? 'text-[#6b8f89] hover:text-[#0f1f1c]'
							: 'text-[#a8d5cc] hover:text-white'
					">
				{{ lang.label }}
			</button>
		</div>

		<!-- Main content -->
		<div class="relative flex-grow">
			<!-- Loading -->
			<div v-if="loading" class="flex flex-col items-center justify-center min-h-screen gap-5">
				<div class="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
					:class="isLight ? 'border-[#1a9b8a]' : 'border-[#a8d5cc]'"></div>
				<p class="text-[11px] font-semibold tracking-widest uppercase"
					:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
					{{ $t("tenant.loading") }}
				</p>
			</div>

			<!-- Error -->
			<div v-else-if="error" class="flex flex-col items-center justify-center min-h-screen gap-4 px-6">
				<div class="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
					<svg class="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
				</div>
				<p class="text-sm text-center" :class="isLight ? 'text-[#0f1f1c]/50' : 'text-white/50'">
					{{ $t("tenant.errorLine1") }}<br />{{ $t("tenant.errorLine2") }}
				</p>
			</div>

			<!-- Content -->
			<div v-else-if="tenantData" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				<!-- Breadcrumb -->
				<div class="flex items-center gap-2 text-[11px] font-medium tracking-wide mb-8">
					<router-link to="/discover" class="transition-colors duration-200" :class="isLight
							? 'text-[#1a9b8a]/60 hover:text-[#1a9b8a]'
							: 'text-[#a8d5cc]/60 hover:text-[#a8d5cc]'
						">
						{{ $t("tenant.breadcrumbHome") }}
					</router-link>
					<span :class="isLight ? 'text-black/20' : 'text-white/20'">›</span>
					<span :class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/40'">{{
						$t("tenant.breadcrumbOrganizer")
					}}</span>
				</div>

				<div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
					<!-- ══════ LEFT — profile card ══════ -->
					<div class="space-y-5 lg:sticky lg:top-8">
						<div class="rounded-2xl border overflow-hidden" :class="isLight
								? 'border-[#1a9b8a]/15 bg-white/60'
								: 'border-white/[0.07] bg-white/[0.03]'
							">
							<!-- Teal band -->
							<div class="h-20 relative" :class="isLight
									? 'bg-gradient-to-r from-[#1a9b8a]/20 to-[#0d6b5e]/10'
									: 'bg-gradient-to-r from-[#1a9b8a]/30 to-[#0d6b5e]/20'
								">
								<svg class="absolute right-3 top-2 w-16 h-16 opacity-10" viewBox="0 0 64 64"
									fill="none">
									<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
										<rect x="16" y="0" width="12" height="12" transform="rotate(45 16 0)" />
										<rect x="36" y="0" width="12" height="12" transform="rotate(45 36 0)" />
										<rect x="6" y="20" width="12" height="12" transform="rotate(45 6  20)" />
										<rect x="26" y="20" width="12" height="12" transform="rotate(45 26 20)" />
										<rect x="46" y="20" width="12" height="12" transform="rotate(45 46 20)" />
									</g>
								</svg>
							</div>

							<!-- Avatar + info -->
							<div class="px-5 pb-6 -mt-10">
								<div class="mb-4">
									<img v-if="tenantData.logo_url" :src="tenantData.logo_url" alt="Logo"
										class="w-20 h-20 rounded-full object-cover shadow-lg" :class="isLight
												? 'border-4 border-[#e4f4f0]'
												: 'border-4 border-[#0f1f1c]'
											" />
									<div v-else
										class="w-20 h-20 rounded-full flex items-center justify-center text-3xl shadow-lg"
										:class="isLight
												? 'border-4 border-[#e4f4f0] bg-[#1a9b8a]/15'
												: 'border-4 border-[#0f1f1c] bg-[#1a9b8a]/20'
											">
										🏢
									</div>
								</div>

								<h1 class="font-['Montserrat'] font-extrabold text-xl tracking-tight mb-1"
									:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
									{{ tenantData.name }}
								</h1>

								<!-- Handle pill -->
								<div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border mb-4"
									:class="isLight
											? 'border-[#1a9b8a]/25 bg-[#1a9b8a]/10 text-[#0d6b5e]'
											: 'border-[#1a9b8a]/25 bg-[#1a9b8a]/10 text-[#a8d5cc]'
										">
									<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
											d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
									</svg>
									{{ tenantData.handle || tenantData.email }}
								</div>

								<!-- Description -->
								<p class="text-sm leading-relaxed mb-5"
									:class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/50'">
									{{ tenantData.description || $t("tenant.noDescription") }}
								</p>

								<!-- Email + Chat -->
								<div class="flex gap-2 mb-3">
									<button @click="sendEmail"
										class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200"
										:class="isLight
												? 'border-[#1a9b8a]/20 text-[#0d6b5e]/70 hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e] hover:bg-[#1a9b8a]/8'
												: 'border-white/[0.08] text-white/60 hover:border-[#1a9b8a]/35 hover:text-[#a8d5cc] hover:bg-[#1a9b8a]/8'
											">
										<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
												d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
										</svg>
										{{ $t("tenant.email") }}
									</button>
									<button @click="openChat"
										class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200"
										:class="isLight
												? 'border-[#1a9b8a]/20 text-[#0d6b5e]/70 hover:border-[#1a9b8a]/40 hover:text-[#0d6b5e] hover:bg-[#1a9b8a]/8'
												: 'border-white/[0.08] text-white/60 hover:border-[#1a9b8a]/35 hover:text-[#a8d5cc] hover:bg-[#1a9b8a]/8'
											">
										<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
												d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
										</svg>
										{{ $t("tenant.whatsapp") }}
									</button>
								</div>

								<!-- Contact CTA -->
								<button @click="contactOrganizer"
									class="w-full py-3 rounded-xl text-sm font-bold tracking-wide bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20 transition-all duration-200">
									{{ $t("tenant.contactOrganizer") }}
								</button>
							</div>
						</div>
					</div>

					<!-- ══════ RIGHT — events ══════ -->
					<div class="lg:col-span-2 space-y-5">
						<!-- Section header — wraps on mobile -->
						<div class="flex flex-wrap items-center justify-between gap-3">
							<div>
								<p class="text-[10px] font-semibold tracking-widest uppercase mb-0.5"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									{{ $t("tenant.eventsLabel") }}
								</p>
								<h2 class="font-['Montserrat'] font-extrabold text-2xl tracking-tight"
									:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
									{{
										eventFilter === "upcoming"
											? $t("tenant.upcoming")
											: $t("tenant.past")
									}}
								</h2>
							</div>

							<!-- Filter pill toggle -->
							<div class="flex items-center gap-1 p-1 rounded-xl border" :class="isLight
									? 'bg-black/[0.03] border-black/[0.06]'
									: 'bg-white/[0.04] border-white/[0.06]'
								">
								<button @click="eventFilter = 'upcoming'"
									class="px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
									:class="eventFilter === 'upcoming'
											? 'bg-[#1a9b8a] text-white shadow-sm'
											: isLight
												? 'text-[#0f1f1c]/50 hover:text-[#0f1f1c]/80'
												: 'text-white/40 hover:text-white/70'
										">
									{{ $t("tenant.upcomingFilter") }}
								</button>
								<button @click="eventFilter = 'past'"
									class="px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
									:class="eventFilter === 'past'
											? 'bg-[#1a9b8a] text-white shadow-sm'
											: isLight
												? 'text-[#0f1f1c]/50 hover:text-[#0f1f1c]/80'
												: 'text-white/40 hover:text-white/70'
										">
									{{ $t("tenant.pastFilter") }}
								</button>
							</div>
						</div>

						<!-- Error state -->
						<div v-if="error"
							class="rounded-2xl border border-red-500/20 bg-red-500/8 px-5 py-4 flex items-start gap-3">
							<svg class="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24"
								stroke="currentColor">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
									d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
							<div>
								<p class="text-sm font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
									{{ $t("tenant.errorTitle") }}
								</p>
								<p class="text-xs mt-0.5" :class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/40'">
									{{ $t("tenant.errorDesc") }}
								</p>
							</div>
						</div>

						<!-- Empty state -->
						<div v-else-if="filteredEvents.length === 0"
							class="flex flex-col items-center justify-center py-24 gap-5">
							<svg class="w-14 h-14" :style="{ opacity: isLight ? 0.2 : 0.15 }" viewBox="0 0 64 64"
								fill="none">
								<rect x="32" y="2" width="38" height="38" transform="rotate(45 32 2)"
									:fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
								<rect x="32" y="14" width="22" height="22" transform="rotate(45 32 14)"
									:fill="isLight ? '#e4f4f0' : '#0f1f1c'" />
								<rect x="32" y="24" width="8" height="8" transform="rotate(45 32 24)"
									:fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
							</svg>
							<p class="font-['Montserrat'] font-extrabold text-xl tracking-tight"
								:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
								{{
									eventFilter === "upcoming"
										? $t("tenant.noUpcoming")
										: $t("tenant.noPast")
								}}
							</p>
							<p class="text-sm text-center max-w-xs"
								:class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/35'">
								{{
									eventFilter === "upcoming"
										? $t("tenant.noUpcomingDesc")
										: $t("tenant.noPastDesc")
								}}
							</p>
						</div>

						<!-- Events grid -->
						<div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
							<EventCardComponent v-for="event in filteredEvents" :key="event.id" :event="event"
								:isLight="isLight" />
						</div>
					</div>
				</div>
			</div>

			<!-- No data -->
			<div v-else class="flex items-center justify-center min-h-screen">
				<p class="text-sm" :class="isLight ? 'text-[#0f1f1c]/35' : 'text-white/35'">
					{{ $t("tenant.noData") }}
				</p>
			</div>
		</div>

		<!-- Footer — wired to toggle -->
		<FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />
	</div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");
</style>
